import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent, KeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';

import { FileDropzone } from './FileDropzone';
import { usePortalDropdownPosition } from '../hooks/usePortalDropdownPosition';
import { computeReportThemeFontComboboxPanelBox } from '../utils/portalDropdownGeometry';

export type ReportThemeEditorTranslate = (message: string) => string;

export type ReportThemeColors = {
  text: string;
  background: string;
  main_dark: string;
  main_light: string;
  complementary_1: string;
  complementary_2: string;
};

type FontSource = 'default' | 'asset' | 'google';

export type ReportThemeFontSlot = {
  source: FontSource;
  assetId: number | null;
  googleFamily: string | null;
};

export type ReportThemeFontAssetRow = {
  id: number;
  original_filename: string;
  extension: string;
  file_size: number;
};

export type ReportThemeGoogleFontFamily = {
  family: string;
  category: string;
};

export type ReportThemeApiRow = {
  id: number;
  name: string;
  is_default: boolean;
  scope: string;
  colors: ReportThemeColors;
  title_font_source: string;
  title_font_asset_id: number | null;
  title_google_font_family: string | null;
  body_font_source: string;
  body_font_asset_id: number | null;
  body_google_font_family: string | null;
};

export type ReportThemeEditorMode =
  | { kind: 'create' }
  | { kind: 'edit'; theme: ReportThemeApiRow };

export type ReportThemeEditorApi = {
  listCompanyFonts: (companyId: number) => Promise<ReportThemeFontAssetRow[]>;
  listGoogleFamilies: () => Promise<ReportThemeGoogleFontFamily[]>;
  uploadCompanyFont: (companyId: number, file: File) => Promise<ReportThemeFontAssetRow>;
};

const GOOGLE_FONT_INITIAL_PEEK = 100;

const FONT_FILE_ACCEPT = '.ttf,.otf,.woff,.woff2';

const isAllowedFontFile = (file: File): boolean => /\.(ttf|otf|woff2?)$/i.test(file.name);


const PANEL_Z_INDEX = 2147483000;

const DEFAULT_COLORS: ReportThemeColors = {
  text: '#333333',
  background: '#ffffff',
  main_dark: '#c13d0c',
  main_light: '#fbe8e0',
  complementary_1: '#1a73e8',
  complementary_2: '#34a853',
};

const INITIAL_THEME_FONT_SLOT: ReportThemeFontSlot = { source: 'asset', assetId: null, googleFamily: null };

function themeFontSlotFromApi(theme: ReportThemeApiRow, slot: 'title' | 'body'): ReportThemeFontSlot {
  const src = slot === 'title' ? theme.title_font_source : theme.body_font_source;
  const assetId = slot === 'title' ? theme.title_font_asset_id : theme.body_font_asset_id;
  const googleFamily = slot === 'title' ? theme.title_google_font_family : theme.body_google_font_family;
  if (src === 'google') {
    return { source: 'google', assetId: null, googleFamily: googleFamily ?? null };
  }
  if (src === 'asset') {
    return { source: 'asset', assetId: assetId ?? null, googleFamily: null };
  }
  return INITIAL_THEME_FONT_SLOT;
}

const normalizeFontSlotForApi = (slot: ReportThemeFontSlot): ReportThemeFontSlot => {
  if (slot.source === 'asset' && (slot.assetId === null || slot.assetId <= 0)) {
    return { source: 'default', assetId: null, googleFamily: null };
  }
  if (slot.source === 'google' && (!slot.googleFamily || slot.googleFamily.trim() === '')) {
    return { source: 'default', assetId: null, googleFamily: null };
  }
  return slot;
};

const buildFontFields = (slot: ReportThemeFontSlot, prefix: 'title' | 'body') => {
  const s = normalizeFontSlotForApi(slot);
  return {
    [`${prefix}_font_source`]: s.source,
    ...(s.source === 'asset' && s.assetId !== null ? { [`${prefix}_font_asset_id`]: s.assetId } : {}),
    ...(s.source === 'google' && s.googleFamily ? { [`${prefix}_google_font_family`]: s.googleFamily } : {}),
  };
};

export const buildReportThemeSaveBody = (
  name: string,
  colors: ReportThemeColors,
  titleFont: ReportThemeFontSlot,
  bodyFont: ReportThemeFontSlot,
): Record<string, unknown> => ({
  name,
  colors,
  ...buildFontFields(titleFont, 'title'),
  ...buildFontFields(bodyFont, 'body'),
});

type ComboRow =
  | { kind: 'none' }
  | { kind: 'font'; row: ReportThemeGoogleFontFamily };

export type ReportThemeGoogleFontComboboxProps = {
  id: string;
  value: string | null;
  onChange: (family: string | null) => void;
  families: ReportThemeGoogleFontFamily[];
  disabled: boolean;
  initialPeekCount: number;
  t: ReportThemeEditorTranslate;
};

export const ReportThemeGoogleFontCombobox = ({
  id,
  value,
  onChange,
  families,
  disabled,
  initialPeekCount,
  t,
}: ReportThemeGoogleFontComboboxProps) => {
  const listboxId = `${id}-listbox`;
  const searchId = `${id}-search`;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);

  const panelBox = usePortalDropdownPosition(open, triggerRef, computeReportThemeFontComboboxPanelBox);

  const trimmed = query.trim().toLowerCase();

  const filteredFonts = useMemo(() => {
    if (trimmed === '') {
      let list = families.slice(0, initialPeekCount);
      if (value) {
        const selected = families.find((f) => f.family === value);
        if (selected && !list.some((f) => f.family === selected.family)) {
          list = [selected, ...list.filter((f) => f.family !== selected.family)].slice(0, initialPeekCount);
        }
      }
      return list;
    }
    return families.filter(
      (f) =>
        f.family.toLowerCase().includes(trimmed) || f.category.toLowerCase().includes(trimmed),
    );
  }, [families, trimmed, value, initialPeekCount]);

  const rows = useMemo((): ComboRow[] => [{ kind: 'none' }, ...filteredFonts.map((row) => ({ kind: 'font' as const, row }))], [filteredFonts]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    setHighlighted((prev) => {
      if (rows.length === 0) {
        return 0;
      }
      return Math.min(prev, rows.length - 1);
    });
  }, [rows.length]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlighted(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) {
      return undefined;
    }
    const el = document.getElementById(`${id}-row-${highlighted}`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, open, rows.length, id]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = (ev: Event) => {
      const target = ev.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (wrapperRef.current?.contains(target)) {
        return;
      }
      const panel = document.getElementById(`${id}-portal-panel`);
      if (panel?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, id]);

  const pickFamily = useCallback((family: string | null) => {
    onChange(family);
    setOpen(false);
    setQuery('');
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }, [onChange]);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((o) => !o);
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setHighlighted(0);
  };

  const onKeyDownPanel = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, rows.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      const item = rows[highlighted];
      if (!item) return;
      e.preventDefault();
      if (item.kind === 'none') {
        pickFamily(null);
      } else {
        pickFamily(item.row.family);
      }
    }
  };

  const displayLabel = value ?? '';

  const activeDescendantId = rows.length > 0 ? `${id}-row-${highlighted}` : undefined;

  const panelNode =
    open && panelBox && typeof document !== 'undefined'
      ? createPortal(
          <div
            id={`${id}-portal-panel`}
            className="themes-tab__font-combobox-panel"
            role="presentation"
            style={{
              position: 'fixed',
              top: panelBox.top,
              left: panelBox.left,
              width: panelBox.width,
              zIndex: PANEL_Z_INDEX,
            }}
            onMouseDown={(ev: ReactMouseEvent) => {
              ev.preventDefault();
            }}
          >
            <div role="none" className="themes-tab__font-combobox-panel-inner" onKeyDown={onKeyDownPanel}>
              <label htmlFor={searchId} className="visually-hidden">
                {t('Filtrer les polices')}
              </label>
              <input
                id={searchId}
                ref={searchRef}
                type="search"
                className="themes-tab__font-combobox-search"
                value={query}
                onChange={onSearchChange}
                placeholder={t('Rechercher une police…')}
                autoComplete="off"
                aria-controls={listboxId}
                aria-activedescendant={activeDescendantId}
              />
              <ul
                ref={listRef}
                id={listboxId}
                className="themes-tab__font-combobox-list"
                role="listbox"
                aria-label={t('Polices Google')}
              >
                {rows.map((item, idx) => {
                  if (item.kind === 'none') {
                    const noneSel = value === null || value === '';
                    const hi = highlighted === idx;
                    return (
                      <li key="none" role="presentation">
                        <button
                          type="button"
                          id={`${id}-row-${idx}`}
                          className={`themes-tab__font-combobox-option${noneSel ? ' themes-tab__font-combobox-option--selected' : ''}${hi ? ' themes-tab__font-combobox-option--highlight' : ''}`}
                          role="option"
                          aria-selected={noneSel}
                          onClick={() => pickFamily(null)}
                        >
                          {t('— Sélectionner une police —')}
                        </button>
                      </li>
                    );
                  }
                  const { row } = item;
                  const selected = value === row.family;
                  const hi = highlighted === idx;
                  return (
                    <li key={row.family} role="presentation">
                      <button
                        type="button"
                        id={`${id}-row-${idx}`}
                        className={`themes-tab__font-combobox-option${selected ? ' themes-tab__font-combobox-option--selected' : ''}${hi ? ' themes-tab__font-combobox-option--highlight' : ''}`}
                        role="option"
                        aria-selected={selected}
                        onClick={() => pickFamily(row.family)}
                      >
                        <span className="themes-tab__font-combobox-option-name">{row.family}</span>
                        <span className="themes-tab__font-combobox-option-cat">{row.category}</span>
                      </button>
                    </li>
                  );
                })}
                {trimmed !== '' && filteredFonts.length === 0 ? (
                  <li className="themes-tab__font-combobox-empty" role="presentation">
                    {t('Aucune police ne correspond.')}
                  </li>
                ) : null}
              </ul>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div ref={wrapperRef} className="themes-tab__font-combobox">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          className="themes-tab__font-combobox-trigger"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={toggleOpen}
          onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleOpen();
            }
            if (e.key === 'ArrowDown' && !open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span className={displayLabel ? 'themes-tab__font-combobox-value' : 'themes-tab__font-combobox-placeholder'}>
            {displayLabel || t('— Sélectionner une police —')}
          </span>
          <span className="themes-tab__font-combobox-chevron" aria-hidden>
            {open ? '▲' : '▼'}
          </span>
        </button>
      </div>
      {panelNode}
    </>
  );
};

const isValidHex = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);

type ColorFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
};

const ColorField = ({ id, label, value, onChange, disabled }: ColorFieldProps) => {
  const pickerRef = useRef<HTMLInputElement>(null);
  const [hexInput, setHexInput] = useState(value);

  useEffect(() => { setHexInput(value); }, [value]);

  const handleHexChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
    setHexInput(raw);
    if (isValidHex(raw)) onChange(raw);
  };

  const handleHexBlur = () => {
    if (!isValidHex(hexInput)) setHexInput(value);
  };

  return (
    <div className="form-field themes-tab__color-field">
      <label htmlFor={id}>{label}</label>
      <div className="themes-tab__color-input-row">
        <button
          type="button"
          className="themes-tab__color-swatch"
          style={{ backgroundColor: value }}
          onClick={() => pickerRef.current?.click()}
          disabled={disabled}
          aria-label={label}
        />
        <input
          ref={pickerRef}
          type="color"
          value={value}
          onChange={(e) => { onChange(e.target.value); setHexInput(e.target.value); }}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          className="themes-tab__color-picker-hidden"
        />
        <input
          id={id}
          type="text"
          value={hexInput}
          onChange={handleHexChange}
          onBlur={handleHexBlur}
          disabled={disabled}
          className="themes-tab__color-hex-input"
          placeholder="#000000"
          maxLength={7}
          spellCheck={false}
        />
      </div>
    </div>
  );
};

type FontSlotPickerProps = {
  slotId: string;
  label: string;
  value: ReportThemeFontSlot;
  onChange: (slot: ReportThemeFontSlot) => void;
  fonts: ReportThemeFontAssetRow[];
  googleFamilies: ReportThemeGoogleFontFamily[];
  companyId: number;
  onFontUploaded: (font: ReportThemeFontAssetRow) => void;
  disabled: boolean;
  api: ReportThemeEditorApi;
  t: ReportThemeEditorTranslate;
};

const FontSlotPicker = ({
  slotId,
  label,
  value,
  onChange,
  fonts,
  googleFamilies,
  companyId,
  onFontUploaded,
  disabled,
  api,
  t,
}: FontSlotPickerProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const sources: { key: Exclude<FontSource, 'default'>; label: string }[] = [
    { key: 'asset', label: t('Déposer un fichier') },
    { key: 'google', label: t('Google Fonts') },
  ];

  const assetTabOn = value.source === 'asset' || value.source === 'default';
  const googleTabOn = value.source === 'google';

  const setSource = (source: Exclude<FontSource, 'default'>) => {
    if (value.source === source) {
      if (source === 'asset') {
        onChange({ ...value, source: 'asset', assetId: null });
      } else {
        onChange({ ...value, source: 'google', googleFamily: null });
      }
      return;
    }
    onChange({ ...value, source });
  };

  const handleFontFile = async (file: File) => {
    if (!isAllowedFontFile(file)) {
      setUploadError(t('Format non pris en charge. Utilisez TTF, OTF ou WOFF.'));
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const uploaded = await api.uploadCompanyFont(companyId, file);
      onFontUploaded(uploaded);
      onChange({ ...value, source: 'asset', assetId: uploaded.id });
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      const trimmed = raw.trim();
      const tooLarge = /^HTTP\s*413$/i.test(trimmed) || /^Upload failed:\s*413$/i.test(trimmed);
      const safe = tooLarge
        ? t(
            'Ce fichier est trop volumineux pour le serveur. Réessayez avec un fichier plus petit ou une police statique / WOFF.',
          )
        : raw && !/<\s*html[\s>]/i.test(raw)
          ? raw
          : t('Erreur lors du téléversement.');
      setUploadError(safe);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="themes-tab__font-slot">
      <p className="themes-tab__font-slot-label">{label}</p>
      <div className="themes-tab__font-source-tabs" role="group" aria-label={label}>
        {sources.map(({ key, label: srcLabel }) => {
          const active = key === 'asset' ? assetTabOn : googleTabOn;
          return (
            <button
              key={key}
              type="button"
              className={`themes-tab__font-source-tab${active ? ' themes-tab__font-source-tab--active' : ''}`}
              onClick={() => setSource(key)}
              disabled={disabled}
              aria-pressed={active}
            >
              {srcLabel}
            </button>
          );
        })}
      </div>

      {(value.source === 'asset' || value.source === 'default') && (
        <div className="themes-tab__font-asset-stack">
          {fonts.length > 0 ? (
            <select
              id={`${slotId}-asset`}
              className="themes-tab__font-select"
              value={value.assetId ?? ''}
              onChange={(e) => onChange({ ...value, assetId: e.target.value ? Number(e.target.value) : null })}
              disabled={disabled || uploading}
              aria-label={t('Police uploadée')}
            >
              <option value="">{t('— Sélectionner une police —')}</option>
              {fonts.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.original_filename}
                </option>
              ))}
            </select>
          ) : null}
          <FileDropzone
            accept={FONT_FILE_ACCEPT}
            disabled={disabled}
            uploading={uploading}
            ariaLabel={t('Uploader une police')}
            primaryIdleText={t('Déposer ou choisir un fichier')}
            primaryUploadingText={t('Téléversement…')}
            secondaryText={t('TTF, OTF, WOFF')}
            error={uploadError || undefined}
            onFileSelected={(file) => { void handleFontFile(file); }}
          />
        </div>
      )}

      {value.source === 'google' && (
        <ReportThemeGoogleFontCombobox
          id={`${slotId}-google`}
          value={value.googleFamily}
          onChange={(family) => onChange({ ...value, googleFamily: family })}
          families={googleFamilies}
          disabled={disabled}
          initialPeekCount={GOOGLE_FONT_INITIAL_PEEK}
          t={t}
        />
      )}
    </div>
  );
};

export type ReportThemeEditorModalProps = {
  mode: ReportThemeEditorMode;
  companyId: number;
  api: ReportThemeEditorApi;
  t: ReportThemeEditorTranslate;
  onClose: () => void;
  onSubmit: (
    name: string,
    colors: ReportThemeColors,
    titleFont: ReportThemeFontSlot,
    bodyFont: ReportThemeFontSlot,
  ) => Promise<void>;
};

const ReportThemeEditorModal = ({ mode, companyId, api, t, onClose, onSubmit }: ReportThemeEditorModalProps) => {
  const initial = mode.kind === 'edit' ? mode.theme : null;
  const [name, setName] = useState(initial?.name ?? '');
  const [colors, setColors] = useState<ReportThemeColors>(initial?.colors ?? DEFAULT_COLORS);
  const [titleFont, setTitleFont] = useState<ReportThemeFontSlot>(() =>
    initial ? themeFontSlotFromApi(initial, 'title') : INITIAL_THEME_FONT_SLOT,
  );
  const [bodyFont, setBodyFont] = useState<ReportThemeFontSlot>(() =>
    initial ? themeFontSlotFromApi(initial, 'body') : INITIAL_THEME_FONT_SLOT,
  );
  const [fonts, setFonts] = useState<ReportThemeFontAssetRow[]>([]);
  const [googleFamilies, setGoogleFamilies] = useState<ReportThemeGoogleFontFamily[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void (async () => {
      const [fontList, familyList] = await Promise.all([
        api.listCompanyFonts(companyId).catch(() => [] as ReportThemeFontAssetRow[]),
        api.listGoogleFamilies().catch(() => [] as ReportThemeGoogleFontFamily[]),
      ]);
      setFonts(fontList);
      setGoogleFamilies(familyList);
    })();
  }, [api, companyId]);

  const colorLabels: Record<keyof ReportThemeColors, string> = {
    text: t('Texte'),
    background: t('Fond'),
    main_dark: t('Principale (foncée)'),
    main_light: t('Principale (claire)'),
    complementary_1: t('Complémentaire 1'),
    complementary_2: t('Complémentaire 2'),
  };

  const handleColorChange = (key: keyof ReportThemeColors) => (v: string) => {
    setColors((prev) => ({ ...prev, [key]: v }));
  };

  const handleFontUploaded = (font: ReportThemeFontAssetRow) => {
    setFonts((prev) => (prev.some((f) => f.id === font.id) ? prev : [font, ...prev]));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSubmit(name.trim(), colors, titleFont, bodyFont);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Erreur lors de la sauvegarde.'));
      setSaving(false);
    }
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return (
    <>
      {createPortal(
    <div className="thn-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="theme-modal-title">
      <div className="thn-modal thn-modal--large">
        <button type="button" className="thn-modal__close" onClick={onClose} aria-label={t('Fermer')}>
          <span aria-hidden="true">×</span>
        </button>
        <div className="thn-modal__header">
          <h3 id="theme-modal-title">
            {mode.kind === 'create' ? t('Créer un thème') : t('Modifier le thème')}
          </h3>
        </div>
        <form onSubmit={(e) => { void handleSubmit(e); }}>
          <div className="thn-modal__body">
            <div className="form-field">
              <label className='h3' htmlFor="theme-modal-name">{t('Nom du thème')}</label>
              <input
                id="theme-modal-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={saving}
              />
            </div>

            <div className="themes-tab__modal-body-grid">

              <h3>{t('Typographies')}</h3>
              <div className='themes-tab__modal-fonts'>
                <FontSlotPicker
                  slotId="title-font"
                  label={t('Typographie titres')}
                  value={titleFont}
                  onChange={setTitleFont}
                  fonts={fonts}
                  googleFamilies={googleFamilies}
                  companyId={companyId}
                  onFontUploaded={handleFontUploaded}
                  disabled={saving}
                  api={api}
                  t={t}
                />
                <FontSlotPicker
                  slotId="body-font"
                  label={t('Typographie texte')}
                  value={bodyFont}
                  onChange={setBodyFont}
                  fonts={fonts}
                  googleFamilies={googleFamilies}
                  companyId={companyId}
                  onFontUploaded={handleFontUploaded}
                  disabled={saving}
                  api={api}
                  t={t}
                />
              </div>

              <div>
                <h3 className="themes-tab__section-label">{t('Couleurs')}</h3>
                <div className="themes-tab__color-grid">
                  {(Object.keys(colorLabels) as (keyof ReportThemeColors)[]).map((key) => (
                    <ColorField
                      key={key}
                      id={`theme-color-${key}`}
                      label={colorLabels[key]}
                      value={colors[key]}
                      onChange={handleColorChange(key)}
                      disabled={saving}
                    />
                  ))}
                </div>
              </div>
            </div>

            {error && <p className="themes-tab__error">{error}</p>}
          </div>
          <div className="thn-modal__footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t('Annuler')}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t('Enregistrement…') : t('Enregistrer')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
      )}
    </>
  );
};

export default ReportThemeEditorModal;
